import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import PageHeader from "../../Layout/Header/PageHeader";
import { plantDefaultValues } from "./PlantDefaultValues";
import Plant from "../Plant";
import { plantSchema } from "./plantSchema";
import { MdmTabs } from "../../Layout/Tabs/MdmTabs";
import { PlantForm } from "./PlantForm";
import { DockAccordion } from "./Docks/DockAccordion";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { isFormSubmitForbidden } from "../../Forms/isFormSubmitForbidden";
import { resetFormStateAndKeepFormValues } from "../../Forms/resetFormStateAndKeepFormValues";
import { PlantOpeningTimesParentForm } from "./OpeningTimes/PlantOpeningTimesParentForm";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  plant: Plant;
  onSave: (plant: Plant) => void;
  onDelete?: (plantId: number | null | undefined) => void;
  disabledFields?: Array<string>;
};

export const PlantFormTabs = ({ title, plant, onSave, onDelete, disabledFields }: Props) => {
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  const methods = useForm<Plant>({
    resolver: yupResolver(plantSchema),
    defaultValues: plantDefaultValues,
    mode: "all",
    disabled: !isUserInAdminNetworkRole,
  });

  useEffect(() => {
    methods.reset(plant);
    return () => methods.reset(plantDefaultValues);
  }, [plant, methods, methods.reset]);

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(`Do you really want to delete plant with Id ${plant?.Id}?`);
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(plant.Id);
  };

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserInAdminNetworkRole}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((currentValues: Plant) => {
            onSave(currentValues);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: "INFORMATION",
              content: <PlantForm disabledFields={disabledFields} plant={plant} />,
            },
            {
              index: 1,
              label: "DOCKS",
              content: (
                <DockAccordion plantId={plant.Id ?? 0} isReadOnly={!isUserInAdminNetworkRole} />
              ),
              disabled: !plant,
            },
            {
              index: 2,
              label: "OPENING TIMES",
              content: <PlantOpeningTimesParentForm parentId={plant.Id ?? 0} />,
              disabled: !plant,
            },
          ]}
        />
      </FormProvider>
    </>
  );
};
